import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Download, AlertTriangle, CheckCircle2, Eye, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable CSV import dialog with an optional dry-run "Preview" step.
 *
 * Flow:
 *   1. User chooses file → clicks "Preview" (dry-run) OR "Import" (skip preview).
 *   2. Backend returns { would_create | would_record, preview: [...], skipped: [...] }.
 *   3. User inspects the diff and clicks "Confirm & Import" — or "Cancel Preview" to
 *      pick a different file.
 *   4. On real import, dialog switches to result mode showing final counts.
 *
 * Props:
 *   open, onOpenChange — visibility
 *   title — dialog heading
 *   sampleUrl, sampleFilename — sample CSV download
 *   importUrl — endpoint that accepts multipart file upload; must support ?dry_run=1
 *   requiredColumns — array of column names shown as a hint
 *   entityLabel — used in copy, e.g. "invoice", "payment"
 *   resultKey — response field with the SUCCESS count for the real import ("created" | "recorded")
 *   previewCountKey — response field with the WOULD count during dry-run ("would_create" | "would_record")
 *   previewColumns — [{ key, label }] describing what to render for each preview row
 *   onDone — called after successful real import (parent should reload data)
 */
export default function ImportCSVDialog({
  open,
  onOpenChange,
  title,
  sampleUrl,
  sampleFilename,
  importUrl,
  requiredColumns = [],
  entityLabel = "row",
  resultKey = "created",
  previewCountKey = "would_create",
  previewColumns,
  onDone,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { would, preview:[], skipped:[] }
  const [result, setResult] = useState(null); // real-import result

  const reset = () => {
    setFile(null);
    setBusy(false);
    setPreview(null);
    setResult(null);
  };

  const downloadSample = async () => {
    try {
      const res = await api.get(sampleUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = sampleFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async ({ dryRun }) => {
    if (!file) return toast.error("Please choose a CSV file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${importUrl}${dryRun ? "?dry_run=1" : ""}`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (dryRun) {
        setPreview(data);
      } else {
        setResult(data);
        const count = data[resultKey] ?? 0;
        toast.success(
          `Imported ${count} ${entityLabel}${count === 1 ? "" : "s"}` +
            (data.skipped?.length ? ` · ${data.skipped.length} skipped` : "")
        );
        if (onDone) onDone();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const defaultPreviewCols = previewColumns || Object.keys(preview?.preview?.[0] || {})
    .filter((k) => k !== "row")
    .map((k) => ({ key: k, label: k }));

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="rounded-sm max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>

        {/* Step 1: file picker */}
        {!preview && !result && (
          <div className="space-y-4">
            <div className="text-sm text-ink-2">
              Upload a CSV file with these columns:
              <div className="mt-1 text-[11px] font-mono bg-[color:var(--bone)] rounded-sm px-2 py-1 break-words">
                {requiredColumns.join(", ")}
              </div>
              <button
                type="button"
                onClick={downloadSample}
                className="mt-2 inline-flex items-center gap-1 text-[color:var(--aisensy-dark)] hover:underline text-xs"
                data-testid="import-download-sample"
              >
                <Download className="w-3.5 h-3.5" /> Download sample CSV
              </button>
            </div>
            <label
              className="flex items-center justify-center gap-2 rounded-sm border border-dashed border-line bg-[color:var(--bone)] px-4 py-6 cursor-pointer hover:bg-white"
              data-testid="import-file-picker"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">{file ? file.name : "Choose CSV file"}</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </label>
            <div className="text-[11px] text-ink-2 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Tip — hit
              <span className="font-semibold text-ink">Preview</span> to see what will change
              before anything is written.
            </div>
          </div>
        )}

        {/* Step 2: preview */}
        {preview && !result && (
          <div className="space-y-4" data-testid="import-preview">
            <div className="text-sm">
              Preview of{" "}
              <span className="font-semibold text-ink">
                {file?.name || "your CSV"}
              </span>
              . Nothing has been written yet.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-line rounded-sm bg-[color:var(--paid-bg)] p-3">
                <div className="eyebrow text-[color:var(--paid)]">
                  Ready to import
                </div>
                <div className="text-2xl font-semibold tabular text-[color:var(--paid)]">
                  {preview[previewCountKey] ?? 0}
                </div>
                <div className="text-[11px] text-ink-2 mt-0.5">
                  {entityLabel}
                  {(preview[previewCountKey] ?? 0) === 1 ? "" : "s"} will be created
                </div>
              </div>
              <div
                className={`border border-line rounded-sm p-3 ${
                  (preview.skipped?.length ?? 0) > 0
                    ? "bg-[color:var(--overdue-bg)]"
                    : "bg-[color:var(--bone)]"
                }`}
              >
                <div className="eyebrow text-[color:var(--overdue)]">Will be skipped</div>
                <div
                  className={`text-2xl font-semibold tabular ${
                    (preview.skipped?.length ?? 0) > 0
                      ? "text-[color:var(--overdue)]"
                      : "text-ink-2"
                  }`}
                >
                  {preview.skipped?.length ?? 0}
                </div>
                <div className="text-[11px] text-ink-2 mt-0.5">
                  rows blocked — see reasons below
                </div>
              </div>
            </div>

            {preview.preview && preview.preview.length > 0 && (
              <div>
                <div className="eyebrow mb-1">
                  First {Math.min(preview.preview.length, 20)} of{" "}
                  {preview.preview.length} rows to be created
                </div>
                <div className="border border-line rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
                  <table
                    className="w-full text-[12px] tabular"
                    data-testid="import-preview-table"
                  >
                    <thead className="bg-[color:var(--bone)]">
                      <tr>
                        <th className="text-left px-2 py-1.5 w-10">Row</th>
                        {defaultPreviewCols.map((c) => (
                          <th key={c.key} className="text-left px-2 py-1.5">
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 20).map((r) => (
                        <tr key={r.row} className="border-t border-line">
                          <td className="px-2 py-1.5 text-ink-2">{r.row}</td>
                          {defaultPreviewCols.map((c) => (
                            <td key={c.key} className="px-2 py-1.5">
                              {r[c.key] == null ? "-" : String(r[c.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.skipped && preview.skipped.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-[color:var(--overdue)] text-sm font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  {preview.skipped.length} row
                  {preview.skipped.length === 1 ? "" : "s"} skipped:
                </div>
                <ul
                  className="mt-2 max-h-48 overflow-y-auto border border-line rounded-sm divide-y divide-[color:var(--line)] text-xs"
                  data-testid="import-preview-skipped"
                >
                  {preview.skipped.map((s) => (
                    <li key={s.row} className="px-3 py-2 tabular">
                      <span className="text-ink-2">Row {s.row}:</span>{" "}
                      <span className="text-ink">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Step 3: final result */}
        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="flex items-center gap-2 text-forest text-sm">
              <CheckCircle2 className="w-4 h-4" />
              <span>
                Imported{" "}
                <span className="font-semibold">{result[resultKey] ?? 0}</span>{" "}
                {entityLabel}
                {(result[resultKey] ?? 0) === 1 ? "" : "s"}.
              </span>
            </div>
            {result.skipped && result.skipped.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-[color:var(--overdue)] text-sm font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  {result.skipped.length} row
                  {result.skipped.length === 1 ? "" : "s"} skipped — fix these and re-import:
                </div>
                <ul
                  className="mt-2 max-h-64 overflow-y-auto border border-line rounded-sm divide-y divide-[color:var(--line)] text-xs"
                  data-testid="import-skipped-list"
                >
                  {result.skipped.map((s) => (
                    <li key={s.row} className="px-3 py-2 tabular">
                      <span className="text-ink-2">Row {s.row}:</span>{" "}
                      <span className="text-ink">{s.reason}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] text-ink-2 mt-2">
                  Tip: header is row 1. Fix the reasons above in your CSV, delete the
                  successfully-imported rows, and upload again.
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
          >
            {result ? "Close" : "Cancel"}
          </Button>
          {!preview && !result && (
            <>
              <Button
                variant="outline"
                onClick={() => submit({ dryRun: true })}
                disabled={!file || busy}
                className="rounded-sm"
                data-testid="import-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                {busy ? "Previewing…" : "Preview"}
              </Button>
              <Button
                onClick={() => submit({ dryRun: false })}
                disabled={!file || busy}
                className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
                data-testid="import-submit"
              >
                {busy ? "Importing…" : "Import now"}
              </Button>
            </>
          )}
          {preview && !result && (
            <>
              <Button
                variant="outline"
                onClick={reset}
                className="rounded-sm"
                data-testid="import-cancel-preview"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Start over
              </Button>
              <Button
                onClick={() => submit({ dryRun: false })}
                disabled={busy || (preview[previewCountKey] ?? 0) === 0}
                className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
                data-testid="import-confirm-submit"
              >
                {busy
                  ? "Importing…"
                  : `Confirm & Import ${preview[previewCountKey] ?? 0} ${entityLabel}${
                      (preview[previewCountKey] ?? 0) === 1 ? "" : "s"
                    }`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
